import {
    Component,
    Event,
    EventEmitter,
    h,
    Host,
    Listen,
    Prop,
    Watch,
} from "@stencil/core";
import { waitForHydrated } from "@trumpf/ux.utils";

const baseClass = "ux-iws-async-modal";
const triggerAttr = "data-trigger-async-modal";
const reloadOnCloseAttr = "data-modal-reload-on-close";

@Component({
    tag: "ux-iws-async-modal",
    styleUrl: "async-modal.scss",
    shadow: false,
})
export class AsyncModal {
    private id = `ux-iws-async-modal-${counter++}`;
    private iframe: HTMLIFrameElement = null;
    private modal: HTMLUxModalElement = null;
    private spinner: HTMLUxSpinnerOverlayElement = null;

    /** identifier for openenig/closing the modal */
    @Prop() readonly name: string | null = null;
    /** Display the modal directly */
    @Prop({ mutable: true, reflect: true }) opened = false;
    /** url of the async content */
    /* eslint-disable-next-line */
    @Prop() src: string | null = null;
    /** The label of the close button for the ux-modal */
    @Prop() readonly closeButtonText: string = "";
    /** Disables the close button in the top corner of the ux-modal */
    @Prop() readonly noCloseButton: boolean = false;
    /** If present, uxModalClose event is emitted when escape key is pressed **/
    @Prop() readonly closeOnEscKey: boolean = false;
    /** If present, uxModalClose event is emitted when the backdrop of ux-modal is clicked **/
    @Prop() readonly closeOnBackdropClick: boolean = false;
    /** reloads the page when the modal is closed */
    @Prop() readonly reloadOnClose: boolean = false;
    /** At which vertical position should the ux-modal be docked **/
    @Prop() readonly stickyPosition: "center" | "bottom" | "top" = "center";

    /** event emitted when the loaded content is inserted in the modal */
    @Event({
        eventName: "uxIwsAsyncModalContentInserted",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    asyncModalContentInserted: EventEmitter;

    private bodyScrollLock(isLocked) {
        if (isLocked) {
            document.body.classList.add("ux-body-scroll-lock");
        } else {
            document.body.classList.remove("ux-body-scroll-lock");
        }
    }

    componentWillLoad() {
        this.initSpinner();
        this.initModal();
    }

    connectedCallback() {
        document.addEventListener("click", this.triggerElementClickHandler);
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.triggerElementClickHandler);
    }

    private initModal() {
        const existingModal = document.querySelector<HTMLUxModalElement>(
            `ux-modal[name="${this.id}"]`
        );
        this.modal =
            existingModal !== null
                ? existingModal
                : document.createElement("ux-modal");
        this.modal.setAttribute("name", this.id);
        if (existingModal === null) {
            document.body.append(this.modal);
        }
        this.updateModal();
    }

    private initSpinner() {
        const existingSpinner = document.querySelector<
            HTMLUxSpinnerOverlayElement
        >(`ux-spinner-overlay`);
        this.spinner =
            existingSpinner !== null
                ? existingSpinner
                : document.createElement("ux-spinner-overlay");
        if (existingSpinner === null) {
            document.body.append(this.spinner);
        }
    }

    @Watch("closeButtonText")
    @Watch("noCloseButton")
    @Watch("closeOnEscKey")
    @Watch("closeOnBackdropClick")
    @Watch("reloadOnClose")
    @Watch("stickyPosition")
    private updateModal() {
        if (this.modal === null) {
            return;
        }
        this.modal.closeButtonText = this.closeButtonText;
        this.modal.noCloseButton = this.noCloseButton;
        this.modal.closeOnEscKey = this.closeOnEscKey;
        this.modal.closeOnBackdropClick = this.closeOnBackdropClick;
        this.modal.reloadOnClose = this.reloadOnClose;
        this.modal.stickyPosition = this.stickyPosition;
    }

    @Listen("uxModalClose", { target: "window" })
    onClose(e: CustomEvent) {
        if (e.detail === this.id) {
            this.opened = false;
        }
    }

    @Watch("opened")
    controlSpinner() {
        if (this.opened) {
            this.bodyScrollLock(true);
            this.spinner.opened = true;
        } else {
            this.bodyScrollLock(false);
        }
    }

    private triggerElementClickHandler = (e: MouseEvent) => {
        const targetElement = (e.target as HTMLElement).hasAttribute(
            triggerAttr
        )
            ? (e.target as HTMLElement)
            : (e.target as HTMLElement).closest(`[${triggerAttr}]`);
        if (targetElement === null) {
            return;
        }
        const trigger: string[] = (
            targetElement.getAttribute(triggerAttr) || ""
        ).split(",");
        if (trigger.length > 1) {
            switch (trigger[0].valueOf()) {
                case "open":
                    if (trigger[1] === this.name && trigger[2] !== undefined) {
                        this.src = trigger[2];
                        this.opened = true;
                    }
                    if (trigger[1] === this.name) {
                        this.opened = true;
                    }
                    break;
                case "close":
                    if (
                        trigger[1] === undefined ||
                        trigger[1] === this.name ||
                        trigger[2] === this.src
                    ) {
                        this.modal.triggerClose();
                    }
                    break;
                default:
                    break;
            }
        }
    };

    private onError() {
        if (!this.opened) {
            return;
        }
        this.spinner.opened = false;
        this.modal.innerHTML = "Error";
        this.modal.opened = true;
    }

    private async onLoad() {
        if (!this.opened || this.modal === null) {
            return;
        }
        this.spinner.opened = false;
        const { body } = this.iframe.contentWindow.document;

        // this logic is adapted from the PL implementation
        if (body.querySelector(".js-detect-login-page") !== null) {
            document.body.dispatchEvent(new CustomEvent("tru:login-required"));
            return;
        }

        const reloadOnCloseElement = body.querySelector(
            `[${reloadOnCloseAttr}]`
        );
        if (reloadOnCloseElement !== null) {
            this.modal.reloadOnClose =
                reloadOnCloseElement
                    .getAttribute(reloadOnCloseAttr)
                    ?.toLowerCase() !== "false";
        }

        const content = body.querySelector("ux-modal-content");
        const footer = body.querySelector("ux-modal-footer");

        const modalContentContainer = await this.getModalContentContainer();
        modalContentContainer.innerHTML = "";
        if (content !== null) {
            modalContentContainer.innerHTML = content.outerHTML;
        }
        // sometimes the ux-modal-footer might be inside the ux-modal-content - which renders a duplicate in the wrong position.
        const footerInContent = modalContentContainer.querySelector(
            "ux-modal-footer"
        );
        if (footerInContent !== null) {
            footerInContent.remove();
        }
        if (footer !== null) {
            modalContentContainer.insertAdjacentHTML(
                "beforeend",
                footer.outerHTML
            );
        }
        if (content === null && footer === null) {
            const container = document.createElement("ux-modal-content");
            container.innerHTML = body.innerHTML;
            modalContentContainer.append(container);
        }
        this.asyncModalContentInserted.emit({
            modalId: this.id,
        });
        Array.from(
            modalContentContainer.querySelectorAll(
                "a:not([target]), form:not([target])"
            )
        ).forEach((element) => {
            element.setAttribute("target", this.id);
        });
        Array.from(
            modalContentContainer.querySelectorAll(`a[target="${this.id}"]`)
        ).forEach((element) => {
            element.addEventListener("click", (e) => {
                !e.defaultPrevented && this.showSpinnerInModal();
            });
        });
        Array.from(
            modalContentContainer.querySelectorAll(`form[target="${this.id}"]`)
        ).forEach((element) => {
            element.addEventListener("submit", (e) => {
                const message = element.getAttribute(
                    "data-async-modal-spinner-message"
                );
                setTimeout(
                    () =>
                        !e.defaultPrevented && this.showSpinnerInModal(message)
                );
            });
        });
        Array.from(
            modalContentContainer.querySelectorAll(
                `[data-trigger-modal="close"]`
            )
        ).forEach((element) => {
            element.setAttribute("data-trigger-modal", `close,${this.id}`);
        });
        this.modal.opened = true;
    }

    private showSpinnerInModal(message?: string) {
        const modalContentContainer = this.getModalContentContainer();

        modalContentContainer.then((modal) => {
            let modalSpinner;

            if (message && message !== "") {
                modalSpinner = document.createElement("ux-iws-spinner-message");
                modalSpinner.setAttribute("message", message);
            } else {
                modalSpinner = document.createElement("ux-spinner-overlay");
            }

            modal.append(modalSpinner);
            modalSpinner.opened = true;
        });
    }

    private getModalContentContainer(): Promise<HTMLElement> {
        return new Promise((resolve) =>
            waitForHydrated(this.modal, () => {
                const slotContainer = this.modal.querySelector<HTMLElement>(
                    ".ux-modal__slot"
                );
                // when modalSlotContainer is not already in the DOM, we add the content directly to this.modal as the slot
                resolve(
                    slotContainer !== null
                        ? slotContainer
                        : (this.modal as HTMLElement)
                );
            })
        );
    }

    render() {
        const { src, opened, id } = this;
        return (
            <Host class={baseClass}>
                <iframe
                    class={`${baseClass}__iframe`}
                    src={opened ? src : undefined}
                    name={id}
                    ref={(el) => (this.iframe = el)}
                    onLoad={this.onLoad.bind(this)}
                    onError={this.onError.bind(this)}
                />
            </Host>
        );
    }
}

let counter = 0;
